<template>
  <v-container class="pa-0 my-3">
    <h4>Period Selection</h4>
    <v-card outlined flat>
      <v-row class="mx-2" align="center">
        <v-col cols="3">
          <v-select
            v-model="range"
            :items="ranges"
            item-text="label"
            item-value="key"
            label="Period"
            persistent-hint
            return-object
            single-line
          ></v-select>
        </v-col>
        <v-col v-if="range.key != 'custom'">
          <span class="subtitle-2">
            {{ period.start | moment("YYYY/MM/DD hh:mm:ss a") }}
          </span>
          
          <v-icon small>mdi-arrow-right</v-icon>
          
          <span class="subtitle-2">
            {{ period.end | moment("YYYY/MM/DD hh:mm:ss a") }}
          </span>
        </v-col>
        <v-col v-else cols="9">
          <v-row align="center" class="mx-0">
            <v-col cols="12" md="5" class="py-0">
              <v-menu
                v-model="menuOpen"
                :close-on-content-click="false"
                :close-on-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    label="Date Range"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    single-line
                    hide-details
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="customRange"
                  :max="(new Date()).toISOString()"
                  show-adjacent-months
                  no-title
                  range
                >
                  <v-spacer></v-spacer>
                  <v-btn
                    @click="closeCustomTimeframe()"
                    color="primary"
                    small
                  >
                    Apply Range
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6" md="3" class="py-0">
              <v-combobox
                v-model="customStartTime"
                :items="startTimeOptions"
                label="Start time"
                prepend-icon="mdi-clock-outline"
                autocomplete="off"
                single-line
                hide-details
                @change="commitStartTime()"
              ></v-combobox>
            </v-col>
            <v-col cols="6" md="3" class="py-0">
              <v-combobox
                v-model="customEndTime"
                :items="endTimeOptions"
                label="End time"
                prepend-icon="mdi-clock-outline"
                autocomplete="off"
                single-line
                hide-details
                @change="commitEndTime()"
              ></v-combobox>
            </v-col>
          </v-row>
          <p v-if="customError" class="caption error--text mb-0 mt-2 mx-3">
            {{ customError }}
          </p>
          <p v-else class="caption text--secondary mb-0 mt-2 mx-3">
            {{ customSummary }}
          </p>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
  import moment from 'moment'

  const INTERVAL_SECONDS = {
    'raw': 60,
    '5m': 5 * 60,
    '1h': 60 * 60,
    '6h': 6 * 60 * 60,
    '1d': 24 * 60 * 60
  }

  // Dropdown granularity only. Anything finer is typed in directly.
  const TIME_OPTION_STEP_MINUTES = 15

  // Offered as the end time on today. Held as a sentinel rather than resolved
  // to a clock time, so the window keeps following the present instead of
  // freezing at whatever "now" was when it was picked. Only the end time
  // offers it -- a start of "now" can only describe an empty window.
  const NOW_OPTION = 'Now'

  // How often the resolved "now" is refreshed. The stamp is minute-granular,
  // so a shorter tick costs a string compare and changes nothing else.
  const NOW_TICK_MS = 30 * 1000
  
  export default {
    name: 'PeriodSelection',
    computed: {
      ranges() {
        var options = [
          {key: '1hr', label: 'Last hour'},
          {key: '6hrs', label: 'Last 6 hours'},
          {key: '1day', label: 'Last day'},
          {key: '2days', label: 'Last 2 days'},
          {key: '5days', label: 'Last 5 days'},
          {key: '14days', label: 'Last 14 days'}
        ]
        if (this.allowMonthlyUsage) {
          options.push({key: 'month', label: 'This month'})
        }
        if (this.allowQuotaPeriod && this.quotaPeriodBounds) {
          options.push({key: 'quota', label: 'Current quota period'})
        }
        options.push({key: 'custom', label: 'Custom Range'})
        return options
      },

      period() {
        if (this.range.key == 'quota') {
          if (!this.quotaPeriodBounds) {
            return null
          }
          const start = moment(this.quotaPeriodBounds.start)
          const resetAt = moment(this.quotaPeriodBounds.resetAt)
          const now = moment(this.nowStamp, 'YYYY-MM-DD HH:mm')
          const end = resetAt.isBefore(now) ? resetAt : now
          const hours = end.diff(start, 'minutes') / 60
          if (!start.isValid() || !end.isValid() || hours <= 0) {
            return null
          }
          const interval = this.intervalForHours(hours)
          return {
            start: start.toISOString(),
            end: end.toISOString(),
            period: INTERVAL_SECONDS[interval],
            key: this.range.key,
            interval,
            endsNow: resetAt.isAfter(now),
          }
        }

        var endTime = moment()
        var endTimeISO = ''
        
        // weird adjustment to land on a period interval and include the last period
        var smallPeriods = ['1hr', '6hrs']
        if (smallPeriods.includes(this.range.key)) {
          endTime.set({minute: 5 * Math.round( endTime.minute() / 5 ), second: 0})
          endTimeISO = endTime.toISOString()
        } else {
          if (endTime.minute() >= 30) {
            endTime.set({minute: 0, second: 0})
            endTimeISO = endTime.add('1', 'hour').toISOString()
          } else {
            endTime.set({minute: 30, second: 0})
            endTimeISO = endTime.toISOString()
          }
        }
        
        if (this.range.key == '1hr') {
          // 1hr, 1min periods
          return { 'start': endTime.subtract('1', 'hour').toISOString(), 'end': endTimeISO, 'period': 1 * 60, 'key': this.range.key, 'interval': 'raw'}
        } else if (this.range.key == '6hrs') {
          // 6hrs, 5min periods
          return { 'start': endTime.subtract('6', 'hour').toISOString(), 'end': endTimeISO, 'period': 5 * 60, 'key': this.range.key, 'interval': '5m'}
        } else if (this.range.key == '2days') {
          // 2days (48hrs ago), 2hr periods
          return { 'start': endTime.subtract('48', 'hour').toISOString(), 'end': endTimeISO, 'period': (60 * 60), 'key': this.range.key, 'interval': '1h'}
        } else if (this.range.key == '5days') {
          // 5days ago, 6hr periods
          return { 'start': endTime.subtract('5', 'day').toISOString(), 'end': endTimeISO, 'period': 6 * (60 * 60), 'key': this.range.key, 'interval': '6h'}
        } else if (this.range.key == '14days') {
          // 14days ago, 24hr periods
          return { 'start': endTime.subtract('13', 'day').toISOString(), 'end': endTimeISO, 'period': 24 * (60 * 60), 'key': this.range.key, 'interval': '6h'}
        } else if (this.range.key == 'month') {
          // API usage billing resets on the first day of each UTC month.
          return { 'start': moment.utc().startOf('month').toISOString(), 'end': moment.utc().toISOString(), 'period': 24 * (60 * 60), 'key': this.range.key, 'interval': '1d'}
        } else if (this.range.key == '30days') {
          // 30days ago, 24hr periods
          return { 'start': endTime.subtract('30', 'day').toISOString(), 'end': endTimeISO, 'period': 24 * (60 * 60), 'key': this.range.key, 'interval': '6h'}
        } else if (this.range.key == 'custom') {
          // Explicit window. Interval follows the standard span rules so a
          // narrow custom range can be inspected at 5m or raw resolution.
          var bounds = this.customBounds
          if (!bounds) {
            return null
          }
          
          var interval = this.intervalForHours(bounds.hours)
          
          // endsNow lets a parent persist the sentinel instead of the resolved
          // bound, so a shared link stays live rather than freezing.
          return { 'start': bounds.start, 'end': bounds.end, 'period': INTERVAL_SECONDS[interval], 'key': this.range.key, 'interval': interval, 'endsNow': this.endsNow }
        } else {
          // 1day (24hrs ago), 30min periods
          return { 'start': endTime.subtract('24', 'hour').toISOString(), 'end': endTimeISO, 'period': 30 * 60, 'key': this.range.key, 'interval': '1h'}
        }
      },

      dateRangeText() {
        return this.customRange.join(' - ')
      },

      nowDate() {
        return this.nowStamp.slice(0, 10)
      },

      nowTime() {
        return this.nowStamp.slice(11)
      },

      endsNow() {
        return this.customEndTime == NOW_OPTION
      },

      customHours() {
        if (!this.customRange || this.customRange.length == 0) {
          return false
        }
        var start = moment(this.customRange[0] + ' ' + this.customStartTime, 'YYYY-MM-DD HH:mm')

        // Reading nowStamp is what ties a "Now" window to the tick.
        var end = this.endsNow
          ? moment(this.nowStamp, 'YYYY-MM-DD HH:mm')
          : moment(this.customRange.slice(-1)[0] + ' ' + this.customEndTime, 'YYYY-MM-DD HH:mm')

        if (!start.isValid() || !end.isValid()) {
          return false
        }
        // The series can only answer with zero-filled buckets past now, so a
        // future end would pad the chart with a flat tail that reads as idle
        // traffic. Clamp instead of trusting the picker, since a restored URL
        // or the default 23:59 end time can both land ahead of now.
        var now = moment()
        if (end.isAfter(now)) {
          end = now
        }
        return { 'start': start, 'end': end, 'hours': end.diff(start, 'minutes') / 60 }
      },

      customStartDate() {
        return this.customRange.slice().sort()[0]
      },

      customEndDate() {
        return this.customRange.slice().sort().slice(-1)[0]
      },

      maxStartTime() {
        return this.latestSelectableTime(this.customStartDate)
      },

      maxEndTime() {
        return this.latestSelectableTime(this.customEndDate)
      },

      startTimeOptions() {
        return this.timeOptionsUpTo(this.maxStartTime, this.customStartTime, false)
      },

      endTimeOptions() {
        return this.timeOptionsUpTo(this.maxEndTime, this.customEndTime, true)
      },

      customBounds() {
        var window = this.customHours
        if (!window || window.hours <= 0 || window.hours > this.maxCustomDays * 24) {
          return false
        }
        return {
          'start': window.start.toISOString(),
          'end': window.end.toISOString(),
          'hours': window.hours
        }
      },

      customError() {
        if (this.range.key != 'custom' || this.customBounds) {
          return ''
        }
        if (this.customHours && this.customHours.hours > this.maxCustomDays * 24) {
          return 'Select ' + this.maxCustomDays + ' days or less. Older data is outside the retention window.'
        }
        return 'End must be after start.'
      },

      customSummary() {
        if (!this.customBounds) {
          return ''
        }
        var interval = this.intervalForHours(this.customBounds.hours)
        var label = interval == 'raw' ? 'raw' : interval
        var text = 'Resolution ' + label + ' for this ' + this.durationText(this.customBounds.hours) + ' window.'
        if (this.endsNow) {
          text += ' Ending now, refreshed every minute.'
        }
        return text
      }
    },
    data() {
      return {
        error: false,
        errorText: '',
        
        range: {key: '1day'},
        
        menuOpen: false,
        
        customRange: [
          moment().subtract('24', 'hour').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD')
        ],

        // Whole days by default, matching the previous date-only behavior.
        customStartTime: '00:00',
        customEndTime: '23:59',

        lastValidStartTime: '00:00',
        lastValidEndTime: '23:59',

        // Reactive stand-in for moment(), so anything derived from "now"
        // recomputes on the tick instead of going stale.
        nowStamp: moment().format('YYYY-MM-DD HH:mm'),
        nowTimer: null
      }
    },
    props: {
      value: {
        default: null
      },
      initialRange: {
        type: String,
        default: null
      },
      // Let a parent restore a shared custom window from the URL.
      initialStart: {
        type: String,
        default: null
      },
      initialEnd: {
        type: String,
        default: null
      },
      maxCustomDays: {
        type: Number,
        default: 14
      },
      allowMonthlyUsage: {
        type: Boolean,
        default: false
      },
      allowQuotaPeriod: {
        type: Boolean,
        default: false
      },
      quotaPeriodBounds: {
        type: Object,
        default: null
      }
    },
    watch: {
      range() {
        // The default end time is 23:59, so switching to custom late in the
        // day would otherwise show a time the window can't actually reach.
        if (this.range.key == 'custom') {
          this.clampTimesToNow()
        }
        this.selectionChanged()
      },
      quotaPeriodBounds() {
        if (this.range.key == 'quota') {
          this.selectionChanged()
        }
      },
      nowStamp() {
        if (this.range.key == 'quota' || (this.range.key == 'custom' && this.endsNow)) {
          this.selectionChanged()
        }
      }
    },
    mounted() {
      if (this.initialRange) {
        this.range = {key: this.initialRange}
      }
      
      if (this.initialRange == 'custom') {
        this.restoreCustomWindow()
        this.clampTimesToNow()
      }

      this.nowTimer = setInterval(() => {
        // Assigning the same minute is a no-op for reactivity, so a "Now"
        // window re-resolves at most once a minute.
        this.nowStamp = moment().format('YYYY-MM-DD HH:mm')
      }, NOW_TICK_MS)

      this.selectionChanged()
    },
    beforeDestroy() {
      clearInterval(this.nowTimer)
    },
    
    methods: {
      intervalForHours(hours) {
        // Finest resolution the API allows for the span, so narrowing the
        // window is what buys detail.
        if (hours <= 1) {
          return 'raw'
        } else if (hours <= 24) {
          return '5m'
        } else if (hours <= 24 * 7) {
          return '1h'
        } else if (hours > 24 * 14 && this.allowMonthlyUsage) {
          return '1d'
        }
        return '6h'
      },

      padTwo(value) {
        return value < 10 ? '0' + value : String(value)
      },

      timeOptionsUpTo(max, selected, allowNow) {
        var options = []
        for (var minutes = 0; minutes < 24 * 60; minutes += TIME_OPTION_STEP_MINUTES) {
          var option = this.padTwo(Math.floor(minutes / 60)) + ':' + this.padTwo(minutes % 60)
          if (max && option > max) {
            break
          }
          options.push(option)
        }

        // The steps stop short of the edge, so make the edge one click. A max
        // means today, whose edge is the sentinel.
        if (!max) {
          if (options.slice(-1)[0] != '23:59') {
            options.push('23:59')
          }
        } else if (allowNow) {
          options.push(NOW_OPTION)
        }

        // An off-step time has to appear in the list, or the combobox filters
        // the menu down to nothing and cannot scroll to the selection. 'Now'
        // sorts after every HH:mm, so it stays last.
        if (selected && selected != NOW_OPTION && options.indexOf(selected) < 0 && this.normalizeTime(selected)) {
          options.push(selected)
          options.sort()
        }

        return options
      },

      normalizeTime(value) {
        if (value === null || value === undefined) {
          return null
        }

        var text = String(value).trim()

        // Forgiving manual entry: 930 and 0905 mean 09:30 and 09:05.
        var colonless = text.match(/^\d{3,4}$/)
        if (colonless) {
          text = text.slice(0, text.length - 2) + ':' + text.slice(-2)
        }

        var parts = text.match(/^(\d{1,2})(?::(\d{1,2}))?$/)
        if (!parts) {
          return null
        }

        var hours = Number(parts[1])
        var mins = parts[2] === undefined ? 0 : Number(parts[2])
        if (hours > 23 || mins > 59) {
          return null
        }

        return this.padTwo(hours) + ':' + this.padTwo(mins)
      },

      commitStartTime() {
        // A combobox hands back whatever was typed, so unparseable text falls
        // back to the last good value rather than blanking the window.
        this.customStartTime = this.normalizeTime(this.customStartTime) || this.lastValidStartTime
        this.clampTimesToNow()
        this.selectionChanged()
      },

      commitEndTime() {
        // Kept as the sentinel rather than resolved, so the window stays live.
        // Only meaningful when the end date is today, which is what a max says.
        if (this.maxEndTime && this.isNowText(this.customEndTime)) {
          this.customEndTime = NOW_OPTION
        } else {
          this.customEndTime = this.normalizeTime(this.customEndTime) || this.lastValidEndTime
        }
        this.clampTimesToNow()
        this.selectionChanged()
      },

      isNowText(value) {
        if (value === null || value === undefined) {
          return false
        }
        return String(value).trim().toLowerCase() == NOW_OPTION.toLowerCase()
      },

      latestSelectableTime(date) {
        // Only today is bounded. Past dates keep the full 24 hours.
        if (!date || date != this.nowDate) {
          return null
        }
        return this.nowTime
      },

      clampTimesToNow() {
        // Zero-padded HH:mm compares correctly as a string. The sentinel is
        // already "as late as possible" and must stay out of that comparison,
        // where it would sort above any clock time and be clamped away.
        if (this.maxStartTime && this.customStartTime > this.maxStartTime) {
          this.customStartTime = this.maxStartTime
        }
        if (!this.endsNow && this.maxEndTime && this.customEndTime > this.maxEndTime) {
          this.customEndTime = this.maxEndTime
        }

        // Every path that settles a time comes through here, so this is the
        // one place that has to remember what typed input can fall back to.
        this.lastValidStartTime = this.customStartTime
        this.lastValidEndTime = this.customEndTime
      },

      durationText(hours) {
        if (hours < 1) {
          return Math.round(hours * 60) + ' minute'
        } else if (hours <= 48) {
          return (Math.round(hours * 10) / 10) + ' hour'
        }
        return (Math.round(hours / 24 * 10) / 10) + ' day'
      },

      restoreCustomWindow() {
        var start = moment(this.initialStart)
        var wantsNow = this.isNowText(this.initialEnd)
        var end = wantsNow ? moment(this.nowStamp, 'YYYY-MM-DD HH:mm') : moment(this.initialEnd)
        if (!this.initialStart || !this.initialEnd || !start.isValid() || !end.isValid()) {
          return
        }
        this.customRange = [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')]
        this.customStartTime = start.format('HH:mm')
        this.customEndTime = wantsNow ? NOW_OPTION : end.format('HH:mm')
      },

      closeCustomTimeframe() {
        // If the user selects a date, and then selects an earlier date, we'll get a 4xx error from the lambdas.
        // Both for the sake of looking pretty, and to fix that, sort the dates before we do anything.
        // 'YYYY-MM-DD' sorts correctly as a string.
        this.customRange = this.customRange.slice().sort()

        // One date is a whole selection now that start and end times exist.
        // Widening it to the previous day would silently turn a 22:00-23:59
        // request into 26 hours.
        if (this.customRange.length == 1) {
          this.customRange = [this.customRange[0], this.customRange[0]]
        }

        // "Now" says nothing about a day that has already ended, so moving the
        // end date off today settles it on that day's real last minute.
        if (this.endsNow && !this.maxEndTime) {
          this.customEndTime = '23:59'
        }

        this.clampTimesToNow()

        this.menuOpen = false

        this.selectionChanged()
      },
      selectionChanged() {
        // An incomplete custom window would blank the chart, so keep the
        // last valid period and let customError explain the problem.
        if (this.period) {
          this.$emit('input', this.period)
        }
      }
    }
  }
</script>